// Chat translation (docs/APP_REFERENCE.md "Chat"): messages in another language show in the reader's own, with the
// original a tap away. The worker translates (worker/translate.js, Cloudflare Workers AI) and keeps each translation
// with its message; this is what the page and the worker share: the languages, the limits, and the page's side of it.
// Pure helpers only, so plain Node can test them (tests/translate.test.mjs).
import { cleanSay, sayLength } from './chat.js';

export const TR_IDS_MAX = 15;               // messages in one request: 15 ids of 13 digits fit the worker's 256-character frame
export const TR_CAP = 4;                    // a page's burst of requests (a full history is two),
export const TR_EVERY_MS = 2000;            // then one more every 2 s
export const TR_MAX = 160;                  // a translation's length at most (code points): twice a message, for the wordier languages
export const TR_TRIES = 3;                  // times a page asks for a message the worker couldn't translate yet
export const TR_RETRY_MS = 30000;           // and how long it waits before asking again

// The languages a message can be translated into, by the code the page asks for (a browser's language, reduced to
// what makes a difference here), with the name the model is told. Chinese is two: the scripts differ.
export const LANGS = {
  en: 'English', es: 'Spanish', pt: 'Portuguese', fr: 'French', de: 'German', it: 'Italian', nl: 'Dutch', ca: 'Catalan',
  sv: 'Swedish', da: 'Danish', no: 'Norwegian', fi: 'Finnish', pl: 'Polish', cs: 'Czech', sk: 'Slovak', hu: 'Hungarian',
  ro: 'Romanian', bg: 'Bulgarian', el: 'Greek', ru: 'Russian', uk: 'Ukrainian', sr: 'Serbian', hr: 'Croatian',
  sl: 'Slovenian', lt: 'Lithuanian', lv: 'Latvian', et: 'Estonian', tr: 'Turkish', ar: 'Arabic', he: 'Hebrew',
  fa: 'Persian', ur: 'Urdu', hi: 'Hindi', bn: 'Bengali', ta: 'Tamil', te: 'Telugu', mr: 'Marathi', id: 'Indonesian',
  ms: 'Malay', fil: 'Filipino', vi: 'Vietnamese', th: 'Thai', ja: 'Japanese', ko: 'Korean', sw: 'Swahili',
  zh: 'Simplified Chinese', 'zh-TW': 'Traditional Chinese',
};
// Codes that mean the same language as one above (old ones, and Norwegian's two written forms).
const ALIASES = { iw: 'he', in: 'id', tl: 'fil', nb: 'no', nn: 'no' };
const TRADITIONAL = /^zh-(?:hant|tw|hk|mo)\b/i;

/**
 * The code for a language tag, as LANGS has it ('pt-BR' → 'pt', 'zh-Hant-HK' → 'zh-TW', 'nb' → 'no'), or, for a
 * language LANGS doesn't have, its own two or three letters ('eu'); null for anything that isn't a language tag.
 */
export function langOf(tag) {
  if (typeof tag !== 'string' || !/^[a-z]{2,3}(?:[-_][a-z0-9]{2,8})*$/i.test(tag)) return null;
  const t = tag.replace(/_/g, '-');
  if (TRADITIONAL.test(t)) return 'zh-TW';
  const base = t.split('-')[0].toLowerCase();
  return ALIASES[base] ?? base;
}

/**
 * Who's reading, from the browser's languages (navigator.languages): `to`, the first one a message can be translated
 * into (null when there's none: no translations), and `known`, every language they read, whose messages stay as written.
 */
export function readersOf(languages) {
  const codes = (Array.isArray(languages) ? languages : []).map(langOf).filter(Boolean);
  return { to: codes.find(c => Object.hasOwn(LANGS, c)) ?? null, known: new Set(codes) };
}

/** Whether a message needs translating for this reader at all: someone else's, with a letter in it (not only emoji). */
export const wantsTranslation = (m, me) => m.a !== me && /\p{L}/u.test(m.text);

/**
 * One of the worker's `translated` list, as the page keeps it: { id, lang } (the language the message is written in;
 * 'und' when it has no words), with `text` when there's a translation that differs from it; null for junk.
 */
export function translationOf(t) {
  const lang = langOf(t?.lang);
  if (!Number.isSafeInteger(t?.id) || !lang) return null;
  if (t.text === undefined) return { id: t.id, lang };
  const text = cleanSay(t.text);
  return text && sayLength(text) <= TR_MAX ? { id: t.id, lang, text } : null;
}

/** A language's name for the page's own words ('Russian'), or null for one it doesn't know. */
export const langName = code => LANGS[code]?.replace(/^(?:Simplified|Traditional) /, '') ?? null;
